// Widget i18n — 轻量 t() 函数，零依赖。
//
// 使用：
//   import { t } from '../i18n';
//   t('input.placeholder')                         → 根据当前 lang 拿字符串
//   t('status.agents_online', { count: 3 })        → 变量插值
//
// 语言选择优先级见 lang-resolution.js（访客手动 > 集成方显式 > 浏览器 > 宿主页 <html lang> > en）。
//
// SUPPORTED_LANGS 外的语言统一回退到 'en'。带地区的标签取主子标签归一：
// zh-CN / zh-TW / zh-HK → zh，pt-BR / pt-PT → pt，en-GB → en，以此类推。
//
// ---------- 按需加载 ----------
// 支持 14 种语言，但 14 份词条全塞进 widget.js 会让 gzip 后体积 +28%，而每个访客
// 实际只用得到一种。所以：
//   - en / zh 同步内置（en 是兜底链的终点，必须随时可用；zh 另有 chat.js 直接
//     引用 locales.zh 判默认文案）
//   - 其余 12 种走 import()，编译成独立 async chunk，用到才下载
// 词条没到位时 t() 自动落到 en，chunk 到了再 bump 一次 langNonce 整树重渲染。

import { locales, SUPPORTED_LANGS, LANG_NATIVE_NAMES } from './locales';
import { resolveLangDetailed, normalizeLang as normalizeWith, initLanguageFields } from './lang-resolution';

let _currentLang = null;

// 访客手动切换语言的持久化 key（header 语言切换器写入）。
// 优先级最高：一旦访客亲手选过语言，覆盖所有自动判定。
export const LANG_STORAGE_KEY = 'lane_widget_lang';

// localStorage 不可用（隐私模式等）时，本次页面内的手动选择仍要算手动。
let _manualLang = null;

function normalizeLang(raw) {
    return normalizeWith(raw, SUPPORTED_LANGS);
}

function readLangEnv() {
    const env = { supported: SUPPORTED_LANGS, stored: _manualLang };
    try {
        if (typeof localStorage !== 'undefined') {
            env.stored = localStorage.getItem(LANG_STORAGE_KEY) || _manualLang;
        }
    } catch (_) {}
    try {
        if (typeof window !== 'undefined') {
            env.customizationLang = window.laneChatCustomizations && window.laneChatCustomizations.lang;
            env.globalLang = window.laneChatLang;
            env.globalLangIsDefault = window.laneChatLangIsDefault === true;
        }
    } catch (_) {}
    try {
        if (typeof document !== 'undefined' && document.documentElement) {
            env.htmlLang = document.documentElement.lang;
        }
    } catch (_) {}
    try {
        if (typeof navigator !== 'undefined') {
            env.navigatorLangs = Array.isArray(navigator.languages) && navigator.languages.length
                ? navigator.languages
                : [navigator.language];
        }
    } catch (_) {}
    return env;
}

export function resolveLang() {
    return resolveLangDetailed(readLangEnv()).lang;
}

// /v1/chat init 要带的语言字段，见 lang-resolution.js。
export function getInitLanguageFields() {
    return initLanguageFields(resolveLangDetailed(readLangEnv()));
}

export function getLang() {
    if (_currentLang) return _currentLang;
    _currentLang = resolveLang();
    // 首次问到语言就把 chunk 拉起来（早于 React mount），把「先显示 en 再刷成
    // 目标语言」的窗口压到最小
    ensureLocale(_currentLang);
    return _currentLang;
}

export function setLang(lang, persist = false) {
    const norm = normalizeLang(lang);
    _currentLang = norm || 'en';
    if (persist) {
        _manualLang = _currentLang;
        try { localStorage.setItem(LANG_STORAGE_KEY, _currentLang); } catch (_) {}
    }
    return _currentLang;
}

// ---------- 语言 chunk 注册表 ----------
// 必须写成字面量 import()：webpack 靠静态分析建 chunk，模板字符串拼路径会把
// 整个目录打成一个 chunk（那就白拆了）。
const LOADERS = {
    es: () => import(/* webpackChunkName: "lc-lang-es" */ './locales/es'),
    pt: () => import(/* webpackChunkName: "lc-lang-pt" */ './locales/pt'),
    fr: () => import(/* webpackChunkName: "lc-lang-fr" */ './locales/fr'),
    de: () => import(/* webpackChunkName: "lc-lang-de" */ './locales/de'),
    it: () => import(/* webpackChunkName: "lc-lang-it" */ './locales/it'),
    ru: () => import(/* webpackChunkName: "lc-lang-ru" */ './locales/ru'),
    tr: () => import(/* webpackChunkName: "lc-lang-tr" */ './locales/tr'),
    ja: () => import(/* webpackChunkName: "lc-lang-ja" */ './locales/ja'),
    ko: () => import(/* webpackChunkName: "lc-lang-ko" */ './locales/ko'),
    th: () => import(/* webpackChunkName: "lc-lang-th" */ './locales/th'),
    id: () => import(/* webpackChunkName: "lc-lang-id" */ './locales/id'),
    hi: () => import(/* webpackChunkName: "lc-lang-hi" */ './locales/hi'),
};

const _pending = {};      // lang -> Promise，去重并发请求
const _listeners = new Set();

// 语言词条到位后的订阅口 —— React 侧用它触发整树重渲染。
// 返回退订函数。
export function onLocaleLoaded(cb) {
    _listeners.add(cb);
    return () => _listeners.delete(cb);
}

// 确保某语言的词条已就位。已内置 / 已加载 → 立刻 resolve 的 Promise。
// 加载失败不抛出：t() 会继续用 en 兜底，聊天照常能用，不能因为一个 chunk 404
// 就让 widget 白屏。
export function ensureLocale(lang) {
    const code = normalizeLang(lang) || 'en';
    if (locales[code]) return Promise.resolve(code);
    if (_pending[code]) return _pending[code];
    const load = LOADERS[code];
    if (!load) return Promise.resolve('en');

    _pending[code] = load()
        .then((mod) => {
            const dict = (mod && (mod.default || mod)) || null;
            if (dict && typeof dict === 'object') {
                locales[code] = dict;
                _listeners.forEach((cb) => { try { cb(code); } catch (_) {} });
            }
            return code;
        })
        .catch(() => {
            delete _pending[code];   // 允许下次重试（网络抖动 / CDN 未刷新）
            return 'en';
        });
    return _pending[code];
}

// 词条是否已就位（同步判断，用于决定要不要等）
export function isLocaleReady(lang) {
    return !!locales[normalizeLang(lang) || 'en'];
}

function interpolate(tpl, vars) {
    if (!vars) return tpl;
    return tpl.replace(/\{(\w+)\}/g, (match, key) =>
        Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
    );
}

export function t(key, vars) {
    const lang = getLang();
    const dict = locales[lang] || locales.en;
    const raw = (dict && dict[key]) || (locales.en && locales.en[key]) || key;
    return interpolate(raw, vars);
}

// 解析 { lang: text } 多语言 map（也接受纯字符串），按当前 widget 语言取值。
// 与服务端 VisitorLanguageResolver::pickLocalized 是同一约定的客户端版本——
// 用于服务端不会预先解析、原样透传给 widget 的字段（例如 home_links 的
// title：不经过 UsageController 的 accept-language 解析，得在渲染时按
// getLang() 取）。解析顺序：当前语言 → 'en' → 第一个非空值。
export function pickLocalized(value) {
    let v = value;
    if (typeof v === 'string') {
        const trimmed = v.trim();
        if (trimmed.startsWith('{')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) v = parsed;
            } catch (_) { /* 不是 JSON，当纯字符串处理 */ }
        }
    }
    if (typeof v === 'string') return v;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return '';

    const lang = getLang();
    if (typeof v[lang] === 'string' && v[lang].trim() !== '') return v[lang];
    if (typeof v.en === 'string' && v.en.trim() !== '') return v.en;
    for (const key of Object.keys(v)) {
        if (typeof v[key] === 'string' && v[key].trim() !== '') return v[key];
    }
    return '';
}

export { SUPPORTED_LANGS, LANG_NATIVE_NAMES, locales };

// 模块加载即开跑：widget.js 一执行就把访客那一种语言的 chunk 排进队，
// 不用等 React mount。
try { getLang(); } catch (_) {}
